/**
 * Badge - Um componente visual para exibir pequenos indicadores de status, contadores ou rótulos.
 *
 * Suporta temas claro e escuro, tamanhos, bordas arredondadas, polimorfismo (via prop `as`),
 * customização de cor via Tailwind ou classe customizada, acessibilidade e memoização.
 *
 * @example
 * // Badge padrão
 * <Badge>Ativo</Badge>
 *
 * @example
 * // Badge com cor e tema escuro
 * <Badge color="danger" theme="dark">Erro</Badge>
 *
 * @example
 * // Badge grande e borda md
 * <Badge size="lg" rounded="md">Grande</Badge>
 *
 * @example
 * // Badge como link
 * <Badge as="a" href="/notificacoes" color="warning">Notificações</Badge>
 *
 * @example
 * // Badge com cor customizada (Tailwind)
 * <Badge colorClassName="bg-pink-500 text-white">Custom</Badge>
 *
 * @example
 * // Badge com ref
 * const badgeRef = useRef<HTMLSpanElement>(null);
 * <Badge ref={badgeRef}>Com Ref</Badge>
 *
 * @example
 * // Badge com ícone e número
 * <Badge><BellIcon className="mr-1" /> 3</Badge>
 *
 * @example
 * // Badge sobreposto a outro elemento (ex: Avatar)
 * <span className="relative">
 *   <Avatar ... />
 *   <Badge className="absolute top-0 right-0">3</Badge>
 * </span>
 *
 * @param {object} props
 * @param {React.ElementType} [props.as='span'] Elemento a ser renderizado (span, a, div, etc)
 * @param {React.ReactNode} props.children Conteúdo do badge (texto, número, ícone, etc)
 * @param {'primary'|'success'|'warning'|'danger'|'neutral'} [props.color='primary'] Cor do badge
 * @param {'light'|'dark'} [props.theme='light'] Tema do badge
 * @param {string} [props.className] Classe CSS adicional
 * @param {React.CSSProperties} [props.style] Estilo inline adicional
 * @param {'full'|'md'|'sm'|'none'} [props.rounded='full'] Grau de arredondamento
 * @param {'sm'|'md'|'lg'} [props.size='sm'] Tamanho do badge
 * @param {string} [props.colorClassName] Classe de cor customizada (sobrescreve color/theme)
 * @param {React.AriaRole} [props.role] Role de acessibilidade (ex: "status" para badges dinâmicos)
 * @param {React.Ref<HTMLElement>} [ref] Ref para o elemento raiz
 */

import React, { forwardRef, memo } from "react";
import type { ComponentPropsWithRef } from "react";
import type { ElementType } from "react";
import clsx from "clsx";

const colorMap = {
  light: {
    primary: "bg-blue-100 text-blue-800",
    success: "bg-green-100 text-green-800",
    warning: "bg-yellow-100 text-yellow-800",
    danger: "bg-red-100 text-red-800",
    neutral: "bg-gray-100 text-gray-800",
  },
  dark: {
    primary: "bg-blue-900 text-blue-200",
    success: "bg-green-900 text-green-200",
    warning: "bg-yellow-900 text-yellow-200",
    danger: "bg-red-900 text-red-200",
    neutral: "bg-gray-800 text-gray-200",
  },
} as const;

const sizeMap = {
  sm: "text-xs px-2 py-0.5",
  md: "text-sm px-3 py-1",
  lg: "text-base px-4 py-1.5",
} as const;

const roundedMap = {
  full: "rounded-full",
  md: "rounded-md",
  sm: "rounded",
  none: "rounded-none",
} as const;

type Color = keyof typeof colorMap["light"];
type Theme = keyof typeof colorMap;
type Size = keyof typeof sizeMap;
type Rounded = keyof typeof roundedMap;

// Utilitário para tipagem polimórfica
type PolymorphicBadgeProps<C extends ElementType, Props = object> = 
  Props & {
    as?: C;
  } & Omit<ComponentPropsWithRef<C>, keyof Props | "as">;

export type BadgeOwnProps = {
  color?: Color;
  theme?: Theme;
  rounded?: Rounded;
  size?: Size;
  colorClassName?: string;
  role?: React.AriaRole;
  ariaLabel?: string; // alterado de "aria-label" para "ariaLabel"
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
};

export type BadgeProps<C extends ElementType = "span"> = PolymorphicBadgeProps<C, BadgeOwnProps>;

export const Badge = memo(
  forwardRef(
    // Use a generic ref type based on the element type C
    function BadgeInner<C extends ElementType = "span">(
      {
        as,
        children,
        color = "primary",
        theme = "light",
        className,
        style,
        rounded = "full",
        size = "sm",
        colorClassName,
        role,
        ariaLabel, // alterado aqui
        ...rest
      }: BadgeProps<C>,
      ref: React.Ref<HTMLElement>
    ) {
      const Component = as || "span";
      const themeValue = colorMap[theme] ? theme : "light";
      const colorValue = colorMap[themeValue][color] ? color : "primary";
      const sizeValue = sizeMap[size] ? size : "sm";
      const roundedValue = roundedMap[rounded] ? rounded : "full";

      return (
        <Component
          ref={ref}
          aria-label={ariaLabel ?? (typeof children === "string" ? children : undefined)}
          className={clsx(
            "inline-flex items-center font-medium select-none transition",
            "font-sans",
            colorClassName || colorMap[themeValue][colorValue],
            sizeMap[sizeValue],
            roundedMap[roundedValue],
            className
          )}
          style={{
            fontFamily: "Inter, sans-serif",
            ...style,
          }}
          role={role}
          {...rest}
        >
          {children}
        </Component>
      );
    }
  )
);

export default Badge;